import { EmptyListContent, PageLoaderFull } from "@jugl-web/ui-components";
import { ErrorBoundary } from "@jugl-web/utils/utils/ErrorBoundary";
import PrivateRoute from "@web-src/features/app/components/PrivateRoute";
import PublicRoute from "@web-src/features/app/components/PublicRoute";
import { PageRoute } from "@web-src/features/app/types";
import { useClaimOauthTokenMutation } from "@web-src/features/auth/authApi";
import { proceedLogin, logout } from "@web-src/features/auth/authSlice";
import { setJwtToken } from "@web-src/features/auth/utils";
import { isAppInDevMode } from "@web-src/modules/common/utils/isAppInDevMode";
import { rootNavigationPageConfig } from "@web-src/modules/navigation/root-config";
import { getEntityPagePath } from "@web-src/modules/navigation/utils/getEntityPagePath";
import { useAppDispatch } from "@web-src/store";
import React, {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  CX_GOOGLE_OAUTH_PENDING_CLAIM_KEY,
  ENTITY_ID_KEY,
  getStorageItem,
} from "@jugl-web/utils/storage";
import { useRestApiProvider, UserRole } from "@jugl-web/rest-api";
import { Alert, Avatar } from "@jugl-web/ui-components/cross-platform";
import { useTranslations } from "@jugl-web/utils";
import { useEntitiesListProvider } from "@web-src/features/entities/providers/EntitiesListProvider/useEntitiesListProvider";
import { getLoginFlow } from "@jugl-web/utils/hooks/useLoginFlow";
import { NavigateToPage } from "./NavigateToPage";

const DebugSubscriptionPage = lazy(
  () => import("@web-src/modules/debug/pages/DebugSubscriptionPage")
);
const DebugSubscriptionV2Page = lazy(
  () => import("@web-src/modules/debug/pages/DebugSubscriptionV2Page")
);
const DebugUserSettingsPage = lazy(
  () => import("@web-src/modules/debug/pages/DebugUserSettingsPage")
);
const DebugMobileAppPage = lazy(
  () => import("@web-src/modules/debug/pages/DebugMobileAppPage")
);

const LoginFlowRedirect: React.FC = () => {
  const flow = getLoginFlow();
  if (flow === "cx") {
    return <Navigate to="/cx/login" />;
  }
  return <Navigate to={`/${PageRoute.login}`} />;
};

// TODO: to remove
const TokenLogin: React.FC = () => {
  const { t } = useTranslations();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [message, setMessage] = useState<string>("Logging in...");
  const [claimOAuthToken] = useClaimOauthTokenMutation();
  const { entitiesApi, authApi } = useRestApiProvider();
  const [claimEntity] = entitiesApi.useClaimEntityMutation();
  const [getRequestCountry] = authApi.useLazyRequestCountryQuery();
  const [claimError, setClaimError] = useState(false);
  const [claimRetrying, setClaimRetrying] = useState(false);
  const pendingClaimRef = useRef<{
    anonymousEntityId: string;
    businessName: string;
  } | null>(null);

  const performSilentClaim = useCallback(
    async (pendingClaim: {
      anonymousEntityId: string;
      businessName: string;
    }) => {
      try {
        const countryResp = await getRequestCountry();
        const detectedCountry = countryResp.data?.country;
        const country =
          detectedCountry && detectedCountry !== "default"
            ? detectedCountry
            : "US";

        const result = await claimEntity({
          entityId: pendingClaim.anonymousEntityId,
          data: {
            country,
            name:
              pendingClaim.businessName ||
              t({
                id: "cx-signup.default-workspace-name",
                defaultMessage: "My Workspace",
              }),
          },
        });

        if ("data" in result) {
          localStorage.removeItem(CX_GOOGLE_OAUTH_PENDING_CLAIM_KEY);
          navigate(`/${result.data.id}/cx/ai-agents`);
          return true;
        }
      } catch {
        // Network-level failures — API errors are handled via the result check above
      }
      setClaimError(true);
      return false;
    },
    [claimEntity, getRequestCountry, navigate, t]
  );

  const login = useCallback(async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const tkn = urlParams.get("tkn");
    if (!tkn) {
      setMessage("No token in get parameters");
      return;
    }
    try {
      const response = await claimOAuthToken({ tkn });
      if (!("data" in response) || !response.data) {
        setMessage("Error");
        return;
      }
      const { token, refreshToken } = response.data;
      if (!token) {
        setMessage("No x-auth-joiint return");
        return;
      }
      // Check for pending CX Google OAuth claim before dispatching proceedLogin
      // to avoid PublicRoute redirecting to the new workspace flow
      const pendingClaim = getStorageItem<{
        anonymousEntityId: string;
        businessName: string;
      }>(CX_GOOGLE_OAUTH_PENDING_CLAIM_KEY);

      if (pendingClaim?.anonymousEntityId) {
        // Store JWT for API auth without triggering Redux auth state change
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        setJwtToken(token, refreshToken!);
        pendingClaimRef.current = pendingClaim;
        const claimed = await performSilentClaim(pendingClaim);
        if (claimed) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          await dispatch(proceedLogin({ token, refreshToken: refreshToken! }));
        }
      } else {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        await dispatch(proceedLogin({ token, refreshToken: refreshToken! }));
      }
    } catch (error) {
      try {
        setMessage(String(error));
      } catch {
        setMessage("Unknown error");
      }
    }
  }, [claimOAuthToken, dispatch, performSilentClaim]);

  const handleRetry = useCallback(async () => {
    if (!pendingClaimRef.current) return;
    setClaimRetrying(true);
    setClaimError(false);
    await performSilentClaim(pendingClaimRef.current);
    setClaimRetrying(false);
  }, [performSilentClaim]);

  const handleCloseError = useCallback(async () => {
    setClaimError(false);
    await dispatch(logout());
    window.location.href = "/cx/trial";
  }, [dispatch]);

  useEffect(() => {
    login();
  }, [login]);

  return (
    <>
      <div>{message}</div>
      <Alert
        isOpen={claimError}
        disableBackdropClose
        title={t({
          id: "feedback.something-went-wrong",
          defaultMessage: "Something went wrong.",
        })}
        content={t({
          id: "cx-signup.claim-failed",
          defaultMessage:
            "We couldn't set up your workspace. Please try again.",
        })}
        buttons={[
          {
            text: t({ id: "common.close", defaultMessage: "Close" }),
            role: "close",
            onClick: () => handleCloseError(),
          },
          {
            text: t({ id: "common.retry", defaultMessage: "Retry" }),
            isLoading: claimRetrying,
            onClick: () => handleRetry(),
          },
        ]}
      />
    </>
  );
};

const InstagramCallbackHandler: React.FC = () => {
  const location = useLocation();
  const code = new URLSearchParams(location.search).get("code");
  const entityId = getStorageItem<string>(ENTITY_ID_KEY);

  if (!entityId || !code) {
    return <NavigateToPage page="entryPoint" />;
  }

  return (
    <NavigateToPage
      page="clientSupport"
      config={{
        queryParams: { code },
        entityId,
      }}
    />
  );
};

const ShopifyCallbackHandler: React.FC = () => {
  const { integrationsApi } = useRestApiProvider();
  const [connectShopify] = integrationsApi.useConnectShopifyAccountMutation();
  const { entitiesState } = useEntitiesListProvider();

  const [status, setStatus] = useState<
    "select-entity" | "connecting" | "success" | "error"
  >("select-entity");

  const adminEntities = useMemo(
    () =>
      entitiesState.entities?.filter((e) => e.role === UserRole.admin) ?? [],
    [entitiesState.entities]
  );

  const getShopifyParams = () => {
    const searchParams = new URLSearchParams(window.location.search);
    return {
      sessionToken: searchParams.get("id_token"),
      hmac: searchParams.get("hmac"),
      shop: searchParams.get("shop"),
    };
  };

  const handleSelectEntity = (entityId: string) => {
    const { sessionToken, hmac, shop } = getShopifyParams();

    setStatus("connecting");

    const storeName = (shop || "").replace(".myshopify.com", "");

    connectShopify({
      entityId,
      data: {
        type: "shopify",
        name: storeName,
        data: {
          session_token: sessionToken || "",
          hmac: hmac || "",
          store: shop || "",
        },
      },
    }).then((result) => {
      setStatus("data" in result ? "success" : "error");
    });
  };

  if (entitiesState.isLoading) {
    return <PageLoaderFull isActive className="h-full w-full" />;
  }

  if (adminEntities.length === 0) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-grey text-center">
          You do not have admin access to any workspace.
        </div>
      </div>
    );
  }

  if (status === "select-entity") {
    const { shop } = getShopifyParams();
    return (
      <div className="mx-auto mt-20 max-w-[480px] px-4">
        <h2 className="text-dark mb-2 text-xl font-semibold">
          Select a workspace
        </h2>
        <p className="text-grey mb-4 text-sm">
          Choose the workspace to connect with your Shopify store
          {shop ? ` (${shop})` : ""}:
        </p>
        <div className="flex flex-col gap-2">
          {adminEntities.map((entity) => (
            <div
              key={entity.id}
              className="flex cursor-pointer items-center gap-3 rounded-2xl bg-[#F9F9FA] px-4 py-3 transition-colors hover:bg-[#F5F5F8]"
              onClick={() => handleSelectEntity(entity.id)}
            >
              <Avatar
                username={entity.name}
                size="lg"
                imageUrl={entity.logoImg}
              />
              <span className="text-dark text-sm font-medium">
                {entity.name}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (status === "connecting") {
    return (
      <div className="flex h-screen items-center justify-center">
        <div>Connecting Shopify account...</div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="flex h-screen items-center justify-center">
        <div>Successfully connected Shopify account.</div>
      </div>
    );
  }

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="text-center">
        <div>Failed to connect Shopify account.</div>
        <button
          type="button"
          className="text-primary-600 mt-4 cursor-pointer underline"
          onClick={() => setStatus("select-entity")}
        >
          Try again
        </button>
      </div>
    </div>
  );
};
// END TODO: to remove

const ConnectionOAuthCallbackHandler: React.FC<{
  provider: "calendly" | "squareup";
}> = ({ provider }) => {
  const navigate = useNavigate();
  const { entitiesState } = useEntitiesListProvider();

  useEffect(() => {
    if (entitiesState.isLoading) return;

    const searchParams = new URLSearchParams(window.location.search);

    const accessToken = searchParams.get("token");
    const refreshToken = searchParams.get("refresh_token");
    if (accessToken && refreshToken) {
      setJwtToken(accessToken, refreshToken);
    }

    const entityIdFromStorage = getStorageItem(ENTITY_ID_KEY) as string | null;
    const entityId =
      entityIdFromStorage || entitiesState.entities?.[0]?.id || "";

    if (!entityId) {
      navigate("/");
      return;
    }

    const error = searchParams.get("error");
    if (error) {
      navigate(`/${entityId}/cx/actions`, {
        state: { connectionError: { provider, error } },
        replace: true,
      });
      return;
    }

    const connectionId = searchParams.get("connection_id");

    navigate(`/${entityId}/cx/actions`, {
      state: connectionId
        ? {
            pendingConnectionStep: { provider, connectionId },
          }
        : undefined,
      replace: true,
    });
  }, [entitiesState.isLoading, entitiesState.entities, navigate, provider]);

  return <PageLoaderFull isActive className="h-full w-full" />;
};

const rootNavigationBasedRoutes = Object.values(rootNavigationPageConfig);

export const AppRoutes = () => {
  const location = useLocation();

  return (
    <Suspense
      fallback={
        <PageLoaderFull
          isActive
          className="relative z-[-1] h-full w-full"
          inPortal={false}
        />
      }
    >
      <Routes location={location} key={location.pathname}>
        {rootNavigationBasedRoutes.map((item, index) => (
          <Route
            key={+index}
            path={
              item.isPrivate && !item.isEntityOptional
                ? getEntityPagePath(item.path)
                : item.path
            }
            element={
              <ErrorBoundary
                fallback={
                  <EmptyListContent
                    type="error"
                    onRefetch={() => window.location.reload()}
                  />
                }
              >
                {item.isPrivate ? (
                  <PrivateRoute
                    component={item.element}
                    entityRequired={!item.isEntityOptional}
                    // requiredModules={item.requiredModules}
                    profileRequired={!item.isProfileOptional}
                    activeSubscriptionRequired={
                      !item.isActiveSubscriptionOptional
                    }
                    anonymousAllowed={item.isAnonymousAllowed}
                    requiredUserRoles={item.requiredUserRoles}
                    requiredCXUserRoles={item.requiredCXUserRoles}
                  />
                ) : (
                  <PublicRoute component={item.element} />
                )}
              </ErrorBoundary>
            }
          />
        ))}
        <Route
          path="/jugl-callback"
          element={<PublicRoute component={<TokenLogin />} />}
        />
        <Route path="/instagram-login" element={<InstagramCallbackHandler />} />
        <Route
          path="/connections/shopify"
          element={
            <PrivateRoute
              component={<ShopifyCallbackHandler />}
              entityRequired={false}
              profileRequired={false}
              activeSubscriptionRequired={false}
            />
          }
        />
        <Route
          path="/connections/calendly"
          element={
            <PrivateRoute
              component={<ConnectionOAuthCallbackHandler provider="calendly" />}
              entityRequired={false}
              profileRequired={false}
              activeSubscriptionRequired={false}
            />
          }
        />
        <Route
          path="/connections/squareup"
          element={
            <PrivateRoute
              component={<ConnectionOAuthCallbackHandler provider="squareup" />}
              entityRequired={false}
              profileRequired={false}
              activeSubscriptionRequired={false}
            />
          }
        />
        {isAppInDevMode() && (
          <>
            <Route
              path="/:entityId/debug/subscription"
              element={<PrivateRoute component={<DebugSubscriptionPage />} />}
            />
            <Route
              path="/:entityId/debug/subscription-v2"
              element={<PrivateRoute component={<DebugSubscriptionV2Page />} />}
            />
            <Route
              path="/:entityId/debug/user-settings"
              element={<PrivateRoute component={<DebugUserSettingsPage />} />}
            />
            <Route
              path="/:entityId/debug/mobile-app"
              element={<PrivateRoute component={<DebugMobileAppPage />} />}
            />
          </>
        )}
        <Route path="*" element={<LoginFlowRedirect />} />
      </Routes>
    </Suspense>
  );
};
